import React, {Component, ErrorInfo, ReactNode} from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Error Boundary component to catch React rendering errors
 * and prevent the entire app from crashing.
 * 
 * Usage:
 * <ErrorBoundary fallback={<div>Something went wrong</div>}>
 *   <ComponentThatMightError />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {hasError: false, error: null}
  }

  static getDerivedStateFromError(error: Error): State {
    return {hasError: true, error}
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    
    // Call optional error handler (for logging to Sentry, etc.)
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  handleRetry = () => {
    this.setState({hasError: false, error: null})
  }

  render() {
    if (this.state.hasError) {
      // Return custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI
      return (
        <div className='d-flex flex-column align-items-center justify-content-center p-10'>
          <i className='fas fa-exclamation-triangle text-warning fs-3x mb-5'></i>
          <h3 className='text-dark mb-3'>Something went wrong</h3>
          <p className='text-muted mb-5'>
            An unexpected error occurred. Please try again.
          </p>
          <button 
            className='btn btn-primary'
            onClick={this.handleRetry}
          >
            <i className='fas fa-redo me-2'></i>
            Try Again
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
